import type { AmbassadorBootstrap } from '@wix/ambassador-bootstrap-plugin';
import type { IHttpClient } from '@wix/http-client';
import type { QueryClient } from '@tanstack/react-query';
import type { ErrorHandlerPublicAPI } from '@wix/error-handler';
import { ConnectorCatalogService } from './connector-catalog.service';
import type { ConnectorCatalogEntry } from './connector-catalog.service';
import { ConnectorConnectionsService } from './connector-connections.service';
import { Connectors } from './connectors';
import type {
  ConnectPayload,
  ConnectResult,
  ConnectorRef,
  DisconnectPayload,
  DisconnectResult,
  IConnector,
  QueryConnectorsOptions,
} from './types';

const key = (appId: string, compId: string): string => `${appId}:${compId}`;

export class ConnectorService {
  private readonly catalog: ConnectorCatalogService;
  private readonly connections: ConnectorConnectionsService;

  constructor(
    rpcClient?: AmbassadorBootstrap,
    httpClient?: IHttpClient,
    queryClient?: QueryClient,
    errorHandler?: ErrorHandlerPublicAPI,
  ) {
    this.catalog = new ConnectorCatalogService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
    this.connections = new ConnectorConnectionsService(
      rpcClient,
      httpClient,
      queryClient,
      errorHandler,
    );
  }

  public async query(
    options: QueryConnectorsOptions = {},
  ): Promise<Connectors> {
    const { filter = 'ALL', paging = {}, connectorRefs } = options;

    if (connectorRefs) {
      return this.queryByRefs(connectorRefs, filter);
    }

    return filter === 'CONNECTED'
      ? this.queryConnectedPage(paging)
      : this.queryCatalogPage(filter, paging);
  }

  // Fetches connector data for specific app+component pairs. The backend filters
  // by appId + componentId, so we only enrich the returned entries with their
  // connection state and apply the requested connected/disconnected filter.
  private async queryByRefs(
    connectorRefs: ConnectorRef[],
    filter: QueryConnectorsOptions['filter'],
  ): Promise<Connectors> {
    if (connectorRefs.length === 0) {
      return new Connectors([], { hasNext: false, total: 0 });
    }

    const appIds = connectorRefs.map((ref) => ref.appId);
    const componentIds = connectorRefs.map((ref) => ref.compId);

    const entries = await this.catalog.getCatalogByAppIds(appIds, componentIds);
    const connectedKeys =
      await this.connections.getConnectedKeysByComponentIds(componentIds);

    let connectors: IConnector[] = entries.map((entry) => ({
      ...entry,
      isConnected: connectedKeys.has(key(entry.appId, entry.compId)),
    }));

    if (filter === 'CONNECTED') {
      connectors = connectors.filter((c) => c.isConnected);
    } else if (filter === 'DISCONNECTED') {
      connectors = connectors.filter((c) => !c.isConnected);
    }

    return new Connectors(connectors, {
      hasNext: false,
      total: connectors.length,
    });
  }

  private async queryConnectedPage({
    limit,
    cursor,
  }: {
    limit?: number;
    cursor?: string;
  }): Promise<Connectors> {
    const [catalog, page] = await Promise.all([
      this.catalog.getCatalog(),
      this.connections.queryConnections({ limit, cursor }),
    ]);

    const catalogByKey = new Map<string, ConnectorCatalogEntry>(
      catalog.map((entry) => [key(entry.appId, entry.compId), entry]),
    );

    const connectors: IConnector[] = page.connectors
      .map((c) => catalogByKey.get(key(c.appId, c.compId)))
      .filter((entry): entry is ConnectorCatalogEntry => Boolean(entry))
      .map((entry) => ({ ...entry, isConnected: true }));

    return new Connectors(connectors, {
      hasNext: page.hasNext,
      nextCursor: page.nextCursor,
    });
  }

  private async queryCatalogPage(
    filter: QueryConnectorsOptions['filter'],
    { limit, cursor }: { limit?: number; cursor?: string },
  ): Promise<Connectors> {
    const page = await this.catalog.getCatalogPage({ limit, cursor });

    const connectedKeys = await this.connections.getConnectedKeysByComponentIds(
      page.entries.map((entry) => entry.compId),
    );

    let connectors: IConnector[] = page.entries.map((entry) => ({
      ...entry,
      isConnected: connectedKeys.has(key(entry.appId, entry.compId)),
    }));

    if (filter === 'DISCONNECTED') {
      connectors = connectors.filter((c) => !c.isConnected);
    }

    return new Connectors(connectors, {
      hasNext: page.hasNext,
      nextCursor: page.hasNext ? page.cursor : undefined,
    });
  }

  public async connect(payload: ConnectPayload): Promise<ConnectResult> {
    try {
      const { authorizationUrl } = await this.connections.startConnect(payload);
      return { authorizationUrl };
    } catch {
      return {};
    }
  }

  public async disconnect({
    compId,
    appId,
  }: DisconnectPayload): Promise<DisconnectResult> {
    try {
      const connectorId = await this.connections.findConnectorId(appId, compId);
      if (!connectorId) {
        return { succeed: false };
      }
      await this.connections.deleteConnector(connectorId);
      return { succeed: true };
    } catch {
      return { succeed: false };
    }
  }
}
